import type { ChartModule } from '../domain/types';

interface ChartFormFieldsProps {
  module: ChartModule;
  dateTime: string;
  question: string;
  gender: '' | 'male' | 'female';
  dateType: 'solar' | 'lunar';
  isLeapMonth: boolean;
  method: 'time' | 'manual';
  lineValues: number[];
  numberValue: number;
  onDateTimeChange: (value: string) => void;
  onQuestionChange: (value: string) => void;
  onGenderChange: (value: '' | 'male' | 'female') => void;
  onDateTypeChange: (value: 'solar' | 'lunar') => void;
  onLeapMonthChange: (value: boolean) => void;
  onMethodChange: (value: 'time' | 'manual') => void;
  onLineValueChange: (index: number, value: number) => void;
  onNumberChange: (value: number) => void;
}

export function ChartFormFields({
  module,
  dateTime,
  question,
  gender,
  dateType,
  isLeapMonth,
  method,
  lineValues,
  numberValue,
  onDateTimeChange,
  onQuestionChange,
  onGenderChange,
  onDateTypeChange,
  onLeapMonthChange,
  onMethodChange,
  onLineValueChange,
  onNumberChange
}: ChartFormFieldsProps) {
  const numberInput = module.form.numberInput;
  const timeLabel = module.form.requiresBirthDetails ? '出生时间' : '排盘时间';

  return (
    <fieldset>
      <legend>填写信息</legend>
      <div className="form-grid">
        <label className="field field-wide">
          <span>{timeLabel}</span>
          <input aria-label={timeLabel} type="datetime-local" value={dateTime} onChange={(event) => onDateTimeChange(event.target.value)} required />
          <small>{module.form.requiresBirthDetails
            ? '请填写真实出生时间，这里不会自动带入当前时间。'
            : '按当前设备的本地时间计算，结果页会显示本次时间。'}</small>
        </label>

        {module.form.requiresBirthDetails && (
          <>
            <label className="field">
              <span>生理性别</span>
              <select value={gender} onChange={(event) => onGenderChange(event.target.value as typeof gender)} required>
                <option value="">请选择</option>
                <option value="male">男</option>
                <option value="female">女</option>
              </select>
            </label>
            <label className="field">
              <span>历法</span>
              <select value={dateType} onChange={(event) => onDateTypeChange(event.target.value as typeof dateType)}>
                <option value="solar">公历</option>
                <option value="lunar">农历</option>
              </select>
            </label>
            {dateType === 'lunar' && (
              <label className="field checkbox-field">
                <input aria-label="这是闰月" type="checkbox" checked={isLeapMonth} onChange={(event) => onLeapMonthChange(event.target.checked)} />
                <span>这是闰月</span>
                <small>只有出生月份确为农历闰月时才勾选。</small>
              </label>
            )}
          </>
        )}

        {module.form.supportsManualLines && (
          <label className="field field-wide">
            <span>起卦方式</span>
            <select value={method} onChange={(event) => onMethodChange(event.target.value as typeof method)}>
              <option value="time">时间起卦</option>
              <option value="manual">手工输入六爻（可复现）</option>
            </select>
          </label>
        )}

        {module.form.supportsManualLines && method === 'manual' && (
          <fieldset className="line-inputs field-wide">
            <legend>六次结果（从初爻到上爻）</legend>
            {lineValues.map((value, index) => (
              <label key={index}>
                <span>第 {index + 1} 爻</span>
                <select value={value} onChange={(event) => onLineValueChange(index, Number(event.target.value))}>
                  <option value={6}>6 老阴</option>
                  <option value={7}>7 少阳</option>
                  <option value={8}>8 少阴</option>
                  <option value={9}>9 老阳</option>
                </select>
              </label>
            ))}
          </fieldset>
        )}

        {numberInput && (
          <label className="field">
            <span>{numberInput.label}</span>
            <input type="number" min={numberInput.min} max={numberInput.max} value={numberValue} onChange={(event) => onNumberChange(Number(event.target.value))} required />
          </label>
        )}

        <label className="field field-wide">
          <span>你想问什么？ <em>选填</em></span>
          <textarea aria-label="你想问什么？" rows={3} maxLength={500} value={question} onChange={(event) => onQuestionChange(event.target.value)} placeholder="例如：这次换工作最该注意什么？不要填写姓名、电话等隐私信息。" />
          <small>{question.length}/500 字。这个问题只帮助 AI 聚焦，不会改变排盘结果。</small>
        </label>
      </div>
    </fieldset>
  );
}
